import { auth } from './firebase';
import type { DocumentMetadata } from '../types';
import { workspaceHeader } from './workspaces';
import { withTeamSelection, noteTeamSelectionFailure } from './teamSelection';

/**
 * Safe fetch wrapper that handles non-JSON error responses gracefully.
 * Automatically attaches Firebase auth token for authenticated endpoints.
 * Use this instead of raw fetch + res.json() to prevent crashes when the
 * server returns an HTML error page (e.g. 404 from a misconfigured proxy).
 *
 * Also attaches the active `X-Workspace-Id` header so every server call is
 * scoped to the current workspace (multi-tenant isolation).
 */
/**
 * Custom fetch wrapper that automatically retries when encountering HTTP 503
 * Service Unavailable errors (or temporary network errors) using exponential backoff.
 */
export async function fetchWithRetry(url: string, options?: RequestInit, retries = 3, backoff = 1000): Promise<Response> {
  try {
    const res = await fetch(url, options);
    if (res.status === 503 && retries > 0) {
      await new Promise(r => setTimeout(r, backoff));
      return fetchWithRetry(url, options, retries - 1, backoff * 2);
    }
    return res;
  } catch (err) {
    if (retries > 0) {
      await new Promise(r => setTimeout(r, backoff));
      return fetchWithRetry(url, options, retries - 1, backoff * 2);
    }
    throw err;
  }
}

export async function safeFetch(url: string, options?: RequestInit) {
  const token = auth?.currentUser ? await auth.currentUser.getIdToken().catch(() => null) : null;
  const headers: Record<string, string> = {
    ...workspaceHeader(),
    ...(options?.headers as Record<string, string>),
  };
  if (token && !headers['Authorization']) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  // Multi-team (Slice 2): org-scoped calls carry the session's selected
  // team as the query param the Slice 1 resolvers read. Non-org calls
  // (applicant / admin / public) are returned unchanged by the helper.
  const finalUrl = typeof url === 'string' ? withTeamSelection(url) : url;
  const res = await fetch(finalUrl, { ...options, headers });
  const contentType = res.headers.get('content-type') || '';
  const data = contentType.includes('application/json') ? await res.json() : {};
  if (!res.ok) {
    // Server-side session revocation (role revoked / member removed / account
    // blocked) → force the client to log out immediately on this device.
    if (res.status === 401 && data?.error === 'SESSION_REVOKED') {
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('session:revoked', { detail: data?.reason || 'Your session was revoked.' }));
      }
    }
    // Multi-team (Slice 2): a 409 TEAM_SELECTION_REQUIRED or 403
    // TEAM_NOT_MEMBER answer means the stored team selection is
    // unusable — clear it and let the TeamSelectionGate re-resolve
    // (picker / remaining team) instead of dead-ending the dashboard.
    noteTeamSelectionFailure(finalUrl, res.status, data);
    // Attach the structured payload (e.g. { code: 'UNMET_GATES', unmetGates })
    // so callers can drive guided flows off it instead of parsing messages.
    const err: any = new Error(data?.error || `HTTP ${res.status}: ${url}`);
    err.status = res.status;
    err.data = data;
    throw err;
  }
  return data;
}

/**
 * Download a binary document (PDF) the same way safeFetch calls JSON APIs:
 * authenticated, scoped to the active workspace via X-Workspace-Id, with the
 * server's actual error body surfaced on failure instead of a generic message.
 */
export async function downloadPdf(url: string): Promise<Blob> {
  const token = auth?.currentUser ? await auth.currentUser.getIdToken().catch(() => null) : null;
  const headers: Record<string, string> = { ...workspaceHeader() };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  const res = await fetch(url, { headers });
  if (!res.ok) {
    const contentType = res.headers.get('content-type') || '';
    let detail = '';
    try {
      detail = contentType.includes('application/json')
        ? (await res.json()).error || JSON.stringify(await res.json())
        : (await res.text()).slice(0, 300);
    } catch { /* non-JSON error page */ }
    throw new Error(detail || `PDF download failed (HTTP ${res.status})`);
  }
  const blob = await res.blob();
  if (!blob || blob.size === 0) throw new Error('PDF download returned an empty file');
  return blob;
}

export function triggerBlobDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/**
 * Save document metadata to Firestore via server-side Admin SDK.
 * This bypasses Firestore security rules that may deny client-side writes.
 */
export async function saveDocumentMetadata(
  applicationId: string,
  docId: string,
  docMeta: Partial<DocumentMetadata>,
  replacesDocId?: string
): Promise<void> {
  const token = auth?.currentUser ? await auth.currentUser.getIdToken().catch(() => null) : null;
  if (!token) throw new Error('Not authenticated');
  const res = await fetch('/api/applications/documents/save', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify({ applicationId, docId, docMeta, replacesDocId }),
  });
  const contentType = res.headers.get('content-type') || '';
  const data = contentType.includes('application/json') ? await res.json() : {};
  if (!res.ok) throw new Error(data?.error || `HTTP ${res.status}`);
}

/**
 * Fire-and-forget: trigger background text extraction on a document right after upload.
 * This caches the extracted text in Firestore so the review endpoint doesn't need
 * to re-download and re-process the file.
 */
export function triggerDocumentExtraction(docId: string, applicationId: string): void {
  safeFetch('/api/documents/extract', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ docId, applicationId }),
  }).catch(() => {
    // Non-critical — extraction will happen on-demand during review if not cached
  });
}
